import { BaseApi, Environment } from '@nextroll/ar-base-api';
import { RequestError, UseRequest } from 'hooks/useRequest';
import {
    ActionPropertyDefinitionInput,
    ActionPropertyValueInput,
    ActionPropertyValuesInput,
} from 'types/propertyGeneration';
import { ActionPropertyValuesResult, RecordField } from 'types/roflWorkflowActionProperty';
import { WorkflowActionService } from 'types/roflWorkflowActions';

const workflowActionsApi = new BaseApi('/rofl-api/v1/workflow-actions/', {
    environment: (process.env.ENVIRONMENT || 'development') as Environment,
});

/**
 * Get the actions for a workflow
 * @param advertisableEid The user's associated advertisable EID.
 * @param signal Optional abortController signal to be able to cancel the fetch
*/
export const getWorkflowActions = async (
    advertisableEid: string,
    signal?: AbortSignal,
): Promise<WorkflowActionService[]> => {
    return workflowActionsApi.get('', { advertisable_eid: advertisableEid }, signal).then(res => res.result || []);
};

/**
 * Get the value (should only be one) for a workflow action property
 * @param advertisableEid The user's associated advertisable EID.
 * @param workflowActionEid The associated workflow action EID.
 * @param propertyName The workflow action property name
 * @param actionPropertyValueInput The input params for the action property value endpoint
 * @param signal Optional abortController signal to be able to cancel the fetch
*/
export const getWorkflowActionPropertyValueSingle = async (
    advertisableEid: string,
    workflowActionEid: string,
    propertyName: string,
    actionPropertyValueInput: ActionPropertyValueInput,
    signal?: AbortSignal,
): Promise<ActionPropertyValuesResult> => {
    return workflowActionsApi
        .post(
            `${workflowActionEid}/properties/${propertyName}/values/single`,
            { advertisable_eid: advertisableEid },
            actionPropertyValueInput,
            signal
        )
        .then(res => res.result);
};

interface WorkflowActionPropertyValuesInput {
    advertisableEid: string;
    workflowActionEid: string;
    propertyName: string;
    actionPropertyValuesInput?: ActionPropertyValuesInput;
    call: UseRequest[0];
    signal?: AbortSignal;
    onError?: (requestError: RequestError) => boolean;
}

/**
 * Get the values (could be multiple) for a workflow action property
 * @param advertisableEid The user's associated advertisable EID.
 * @param workflowActionEid The associated workflow action EID.
 * @param propertyName The workflow action property name
 * @param call The useRequest call function
 * @param signal Optional abortController signal to be able to cancel the fetch
 * @param onError Optional error handler for the useRequest hook
*/
export const getWorkflowActionPropertyValues = async ({
    advertisableEid,
    workflowActionEid,
    propertyName,
    actionPropertyValuesInput,
    call,
    signal,
    onError,
}: WorkflowActionPropertyValuesInput): Promise<ActionPropertyValuesResult[]> => {
    return call(async () => {
        const result = await workflowActionsApi
            .post(
                `${workflowActionEid}/properties/${propertyName}/values`,
                { advertisable_eid: advertisableEid },
                actionPropertyValuesInput,
                signal
            )
            .then(res => res.result || []);
        return result;
    }, { errorHandler: (requestError: RequestError) => {
        return onError?.(requestError) ?? false;
    } }) as Promise<ActionPropertyValuesResult[]>;
};

/**
 * Get the definition for a workflow action property
 * @param advertisableEid The user's associated advertisable EID.
 * @param workflowActionEid The associated workflow action EID.
 * @param propertyName The workflow action property name
 * @param actionPropertyDefinitionInput The input params for the action property definition endpoint
 * @param signal Optional abortController signal to be able to cancel the fetch
 */
const getWorkflowActionPropertyDefinition = async (
    advertisableEid: string,
    workflowActionEid: string,
    propertyName: string,
    actionPropertyDefinitionInput: ActionPropertyDefinitionInput,
    signal?: AbortSignal,
): Promise<RecordField[]> => {
    return workflowActionsApi
        .post(
            `${workflowActionEid}/properties/${propertyName}/definition`,
            { advertisable_eid: advertisableEid },
            actionPropertyDefinitionInput,
            signal
        )
        .then(res => res.result || []);
};

/**
 * PropertyDefinitionLookupConfig is the config for calling the definition endpoint for a property
 */
export interface PropertyDefinitionLookupConfig {
    advertisableEid: string;
    workflowActionEid: string;
    propertyName: string;
    properties: Record<string, any>;
    abortSignal?: AbortSignal;
    call: UseRequest[0];
    onError?: (requestError: RequestError) => boolean;
}

/**
 * propertyDefinitionLookup calls rofl api to get the definition of a property
 */
export const propertyDefinitionLookup = async (props: PropertyDefinitionLookupConfig) => {
    const { call, advertisableEid, workflowActionEid, propertyName, properties, abortSignal, onError } = props;
    return call(async () => {
        return getWorkflowActionPropertyDefinition(
            advertisableEid,
            workflowActionEid,
            propertyName,
            {
                properties
            },
            abortSignal
        );
    }, { errorHandler: (requestError: RequestError) => {
        return onError?.(requestError) ?? false;
    } }) as Promise<RecordField[]>;
};

/**
 * SinglePropertyValueLookupConfig is the config for calling the single lookup value endpoint
 */
export interface SinglePropertyValueLookupConfig {
    lookupText: string;
    advertisableEid: string;
    workflowActionEid: string;
    propertyName: string;
    abortSignal?: AbortSignal;
    call: UseRequest[0];
    onError?: (requestError: RequestError) => boolean;
}

/**
 * singlePropertyValueLookup calls rofl api to get a lookup value
 */
export const singlePropertyValueLookup = async (props: SinglePropertyValueLookupConfig) => {
    const { call, advertisableEid, workflowActionEid, propertyName, lookupText, abortSignal, onError } = props;
    return call(async () => {
        return getWorkflowActionPropertyValueSingle(
            advertisableEid,
            workflowActionEid,
            propertyName,
            {
                text: lookupText
            },
            abortSignal
        );
    }, { errorHandler: (requestError: RequestError) => {
        return onError?.(requestError) ?? false;
    } }) as Promise<ActionPropertyValuesResult>;
};
